import { createRef, FC, useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faCircleCheck,
  faCircleXmark,
  faSpinner,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import ModalComponent from "src/core/components/modal/modal.component";
import FormBuilderComponent, {
  FormConfig,
} from "src/shared/form-builder/form-builder.component";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { getSiteByTCCode } from "src/store/slices/location-site.slice";
import { getBlocksBySiteCode } from "src/store/slices/location-block.slice";
import {
  downloadBySiteOrBlock,
  compareAssetLocationFromExcel,
  updateAssetLocationFromExcel,
} from "src/store/slices/asset.slice";
import {
  excelFileTypes,
  validateExcelFile,
} from "src/shared/helpers/validate-Files";
import AssetPreviewTableComponent from "./asset-tagging-modal/asset-preview-table.component";
import capitalizeFirst from "src/shared/helpers/upper-case";
import TableSort from "src/shared/helpers/table-sort";

interface AssetTaggingModalComponentProps {
  showAssetTaggingModal: boolean;
  setShowAssetTaggingModal: (show: boolean) => void;
}

enum Phase {
  SHOW_OPTIONS = "showOptions",
  FILTER_ASSETS = "filterAssets",
  GENERATE_ASSET_LIST = "generateAssetList",
  UPLOAD_ASSET_LIST = "uploadAssetList",
  REVIEW_ASSET_LIST = "reviewAssetList",
  CONFIRM_ASSET_LIST = "confirmAssetList",
}

enum Options {
  GENERATE = "generate",
  PROCESS = "process",
}

type ExportStatus = "idle" | "loading" | "success" | "error";
type ReviewStatus =
  | "idle"
  | "loading"
  | "assetListPreview"
  | "confirmBaselineAndProcess"
  | "error";
type ConfirmAndProcessStatus = "idle" | "loading" | "success" | "error";

const DEFAULT_MODAL_HEADER_TITLES: Record<Phase, string> = {
  [Phase.SHOW_OPTIONS]: "Asset Tagging",
  [Phase.FILTER_ASSETS]: "Generate Asset List",
  [Phase.GENERATE_ASSET_LIST]: "Generate Asset List",
  [Phase.UPLOAD_ASSET_LIST]: "Process Asset List",
  [Phase.REVIEW_ASSET_LIST]: "Preview Asset List",
  [Phase.CONFIRM_ASSET_LIST]: "Process Asset List",
};

const EXPORT_STATUS_MODAL_TITLES: Partial<Record<ExportStatus, string>> = {
  loading: "Generating Asset List...",
  error: "Error Generating Asset List",
  success: "Asset List Generated",
};

const REVIEW_STATUS_MODAL_TITLES: Partial<Record<ReviewStatus, string>> = {
  loading: "Comparing Asset List...",
  error: "Error Previewing Asset List",
  confirmBaselineAndProcess: "Confirm & Process Asset List",
  assetListPreview: "Preview Asset List",
};

const CONFIRM_STATUS_MODAL_TITLES: Partial<
  Record<ConfirmAndProcessStatus, string>
> = {
  loading: "Processing Asset List...",
  error: "Error Processing Asset List",
  success: "Asset List Processed",
};

const resolveModalHeaderTitle = (
  phase: Phase,
  exportStatus: ExportStatus,
  reviewStatus: ReviewStatus,
  confirmAndProcessStatus: ConfirmAndProcessStatus,
): string => {
  const statusTitleByPhase: Partial<Record<Phase, string | undefined>> = {
    [Phase.GENERATE_ASSET_LIST]: EXPORT_STATUS_MODAL_TITLES[exportStatus],
    [Phase.REVIEW_ASSET_LIST]: REVIEW_STATUS_MODAL_TITLES[reviewStatus],
    [Phase.CONFIRM_ASSET_LIST]:
      CONFIRM_STATUS_MODAL_TITLES[confirmAndProcessStatus],
  };

  return (
    statusTitleByPhase[phase] ??
    DEFAULT_MODAL_HEADER_TITLES[phase] ??
    "Asset Tagging"
  );
};

const AssetTaggingModalComponent: FC<AssetTaggingModalComponentProps> = ({
  showAssetTaggingModal,
  setShowAssetTaggingModal,
}) => {
  const formRef = createRef();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>(Phase.SHOW_OPTIONS);
  const [selectedOption, setSelectedOption] = useState<Options>(
    Options.GENERATE,
  );
  const [tcOptions, setTCOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [blockOptions, setBlockOptions] = useState<any>([]);
  const [selectedTcCode, setSelectedTcCode] = useState(null);
  const [selectedSiteCode, setSelectedSiteCode] = useState(null);
  const [formFilterInitialValues, setFormFilterInitialValues] = useState({
    townCouncil: "",
    site: "",
    block: "",
  });
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [exportStatus, setExportStatus] = useState<ExportStatus>("idle");
  const [exportSiteLabel, setExportSiteLabel] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortBy, setSortBy] = useState<any>(capitalizeFirst("assetCode"));
  const [sortDirection, setSortDirection] = useState<any>("desc");
  const [operationKey, setOperationKey] = useState<string>("");
  const [reviewStatus, setReviewStatus] = useState<ReviewStatus>("idle");
  const [reviewData, setReviewData] = useState<any[]>([]);
  const [isReviewDataLoading, setIsReviewDataLoading] =
    useState<boolean>(false);
  const [reviewTotal, setReviewTotal] = useState<number>(0);
  const [changedCount, setChangedCount] = useState<number>(0);
  const [changedOnly, setChangedOnly] = useState<boolean>(false);
  const [baselineLessThanTwelveCount, setBaselineLessThanTwelveCount] =
    useState<number>(0);
  const [confirmAndProcessStatus, setConfirmAndProcessStatus] =
    useState<ConfirmAndProcessStatus>("idle");

  const [columns, setColumns] = useState<any[]>([
    {
      title: "Asset Code",
      label: "assetCode",
      isSorted: true,
    },
    {
      title: "Block Code",
      label: "blockCode",
    },
    {
      title: "Floor Code",
      label: "level",
    },
    {
      title: "Sensor Display Name",
      label: "sensorDisplayName",
    },
    {
      title: "Display Block",
      label: "displayBlock",
    },
    {
      title: "Display Level",
      label: "level",
    },
    {
      title: "Display Location Name",
      label: "displayLocation",
    },
    {
      title: "Location Descriptor",
      label: "locationDescriptor",
    },
    {
      title: "Serial Number",
      label: "sensorSerial",
    },
    {
      title: "Baseline",
      label: "baseline",
    },
    {
      title: "Rated Power",
      label: "lightPower",
    },
  ]);

  const dispatch = useDispatch();
  const townCouncilData = useSelector((state: any) => {
    return state.townCouncil.townCouncils;
  });
  const siteDataByTCCode = useSelector((state: any) => {
    return state.locationSite.sitesByTCCode;
  });
  const blockDataSiteCode = useSelector((state: any) => {
    return state.locationBlock.blocksBySiteCode;
  });

  useEffect(() => {
    const tcs = townCouncilData?.data?.map((tc: any) => ({
      label: tc?.name,
      value: tc?.tcCode,
    }));

    setTCOptions(tcs);
  }, [townCouncilData]);

  useEffect(() => {
    if (!selectedTcCode) return;

    const sites = siteDataByTCCode?.data?.map((site: any) => ({
      label: site?.name,
      value: site?.siteCode,
    }));

    if (!sites?.length) return;

    setSiteOptions(sites);
  }, [siteDataByTCCode, selectedTcCode]);

  useEffect(() => {
    if (!selectedSiteCode) return;

    const blocks = blockDataSiteCode?.data?.map((block: any) => ({
      label: block?.blockName,
      value: block?.blockCode,
    }));

    setBlockOptions(blocks);
  }, [blockDataSiteCode, selectedSiteCode]);

  const handleChangePage = useCallback((page: number): void => {
    setPageNumber(page);
  }, []);

  const handleChangePageSize = useCallback((size: number): void => {
    setPageSize(size);
    setPageNumber(1); // reset to first page whenever page size changes
  }, []);

  const handleChangedOnlyToggle = useCallback((next: boolean): void => {
    setChangedOnly(next);
    setPageNumber(1); // reset to first page when filter changes
  }, []);

  // re-fetch when page, page-size, sort, or changedOnly filter changes inside the preview
  useEffect(() => {
    if (
      phase === Phase.REVIEW_ASSET_LIST &&
      reviewStatus === "assetListPreview" &&
      operationKey
    ) {
      fetchReviewPage(operationKey, pageNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize, sortBy, sortDirection, changedOnly]);

  const onChangeTC = (selectedTC: any) => {
    setSiteOptions([]);
    setBlockOptions([]);
    setSelectedTcCode(selectedTC);
    setSelectedSiteCode(null);

    const form: any = formRef.current;

    form.setFieldValue("site", "");
    form.setFieldValue("block", "");

    if (!selectedTC) return;

    setFormFilterInitialValues((initval: any) => {
      return {
        ...initval,
        townCouncil: selectedTC || "",
        site: "",
        block: "",
      };
    });

    dispatch(getSiteByTCCode({ tcCode: selectedTC }));
  };

  const onChangeSite = (selectedSite: any) => {
    setBlockOptions([]);
    setSelectedSiteCode(selectedSite);

    const form: any = formRef.current;

    form.setFieldValue("block", "");

    if (!selectedSite) return;

    setFormFilterInitialValues((initval: any) => {
      return {
        ...initval,
        site: selectedSite || "",
        block: "",
      };
    });

    dispatch(getBlocksBySiteCode({ siteCode: selectedSite }));
  };

  const onChangeBlock = (selectedBlock: any) => {
    setFormFilterInitialValues((initval: any) => {
      return {
        ...initval,
        block: selectedBlock || "",
      };
    });
  };

  const handleDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const processFile = useCallback(async (file: File) => {
    const isValid = await validateExcelFile(file);
    if (isValid) {
      setUploadedFile(file);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) processFile(file);
    },
    [processFile],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
      // reset input so the same file can be re-selected
      e.target.value = "";
    },
    [processFile],
  );

  const handleChangeSort = useCallback(
    async (sortBy: string, sortDirection: any) => {
      if (!sortBy || !sortDirection) return;
      setSortBy(capitalizeFirst(sortBy));
      setSortDirection(sortDirection);

      const copyColumns: any = await TableSort(columns, sortBy);
      setColumns(copyColumns);
    },
    [columns],
  );

  const filterFormConfig: FormConfig = {
    controls: [
      {
        name: "townCouncil",
        label: "Town Council",
        placeholder: "Select Town Council",
        options: tcOptions,
        onChange: onChangeTC,
        type: "select",
        required: true,
      },
      {
        name: "site",
        label: "Site",
        placeholder: "Select Site",
        options: siteOptions,
        onChange: onChangeSite,
        type: "select",
        required: true,
      },
      {
        name: "block",
        label: "Block",
        placeholder: "Select Block",
        options: blockOptions,
        onChange: onChangeBlock,
        type: "select",
        required: false,
      },
    ],
  };

  const validationSchema = yup.object().shape({
    townCouncil: yup.string().required("Town council is required"),
    site: yup.string().required("Site is required"),
    block: yup.string().optional(),
  });

  const handleCloseModal = async () => {
    setShowAssetTaggingModal(false);
    setPhase(Phase.SHOW_OPTIONS);
    setSelectedOption(Options.GENERATE);

    // reset all the states
    setSelectedTcCode(null);
    setSiteOptions([]);
    setSelectedSiteCode(null);
    setBlockOptions([]);
    setFormFilterInitialValues({
      townCouncil: "",
      site: "",
      block: "",
    });
    setUploadedFile(null);
    setIsDragging(false);
    setExportStatus("idle");
    setExportSiteLabel("");
    setReviewStatus("idle");
    setReviewData([]);
    setReviewTotal(0);
    setChangedCount(0);
    setChangedOnly(false);
    setOperationKey("");
    setPageNumber(1);
    setBaselineLessThanTwelveCount(0);
    setPageSize(10);
    setConfirmAndProcessStatus("idle");
    setSortBy(capitalizeFirst("assetCode"));
    setSortDirection("desc");
    const copyColumns: any = await TableSort(
      columns,
      capitalizeFirst("assetCode"),
    );
    setColumns(copyColumns);
    setIsReviewDataLoading(false);
  };

  const handleNext = () => {
    if (phase === Phase.SHOW_OPTIONS && selectedOption === Options.GENERATE) {
      setPhase(Phase.FILTER_ASSETS);
    } else if (
      phase === Phase.SHOW_OPTIONS &&
      selectedOption === Options.PROCESS
    ) {
      setPhase(Phase.UPLOAD_ASSET_LIST);
    }
  };

  const handleBack = async () => {
    if (phase === Phase.FILTER_ASSETS) {
      setSelectedTcCode(null);
      setSiteOptions([]);
      setSelectedSiteCode(null);
      setBlockOptions([]);
      setFormFilterInitialValues({
        townCouncil: "",
        site: "",
        block: "",
      });
      setSelectedOption(Options.GENERATE);
      setPhase(Phase.SHOW_OPTIONS);
    } else if (phase === Phase.GENERATE_ASSET_LIST) {
      setExportStatus("idle");
      setExportSiteLabel("");
      setPhase(Phase.FILTER_ASSETS);
    } else if (phase === Phase.UPLOAD_ASSET_LIST) {
      setUploadedFile(null);
      setIsDragging(false);
      setSelectedOption(Options.PROCESS);
      setPhase(Phase.SHOW_OPTIONS);
    } else if (phase === Phase.REVIEW_ASSET_LIST) {
      if (reviewStatus === "confirmBaselineAndProcess") {
        setReviewStatus("assetListPreview");
        return;
      }
      setSortBy(capitalizeFirst("assetCode"));
      setSortDirection("desc");
      const copyColumns: any = await TableSort(
        columns,
        capitalizeFirst("assetCode"),
      );
      setColumns(copyColumns);
      setReviewStatus("idle");
      setReviewData([]);
      setReviewTotal(0);
      setOperationKey("");
      setPageNumber(1);
      setUploadedFile(null);
      setIsDragging(false);
      setChangedCount(0);
      setChangedOnly(false);
      setBaselineLessThanTwelveCount(0);
      setPageSize(10);
      setIsReviewDataLoading(false);
      setPhase(Phase.UPLOAD_ASSET_LIST);
    } else if (phase === Phase.CONFIRM_ASSET_LIST) {
      setConfirmAndProcessStatus("idle");
      setPhase(Phase.REVIEW_ASSET_LIST);
      setReviewStatus("assetListPreview");
    }
  };

  const handleGenerateAssetList = () => {
    const form: any = formRef.current;
    if (!form) return;

    form.handleSubmit();
    const { townCouncil, site, block } = form.values;
    if (!townCouncil || !site) return;

    // capture the human-readable site label for the success message
    const label =
      siteOptions?.find((s: any) => s.value === site)?.label ?? site;
    setExportSiteLabel(label);

    setExportStatus("loading");
    setPhase(Phase.GENERATE_ASSET_LIST);

    dispatch(
      downloadBySiteOrBlock({
        siteCode: site ?? null,
        blockCode: block ?? null,
      }),
    )
      .unwrap()
      .then(() => setExportStatus("success"))
      .catch(() => setExportStatus("error"));
  };

  const fetchReviewPage = useCallback(
    (key: string, page: number) => {
      setIsReviewDataLoading(true);
      dispatch(
        compareAssetLocationFromExcel({
          file: null,
          operationKey: key,
          pageNumber: page,
          pageSize,
          sortBy,
          sortDirection,
          isChanged: changedOnly,
        }),
      )
        .unwrap()
        .then((res: any) => {
          setReviewData(res?.data?.rows ?? []);
          setReviewTotal(res?.numberOfRecords ?? 0);
          setChangedCount(res?.data?.changedCount ?? 0);
          setReviewStatus("assetListPreview");
        })
        .catch(() => {
          setReviewStatus("error");
        })
        .finally(() => {
          setIsReviewDataLoading(false);
        });
    },
    [dispatch, pageSize, sortBy, sortDirection, changedOnly],
  );

  const handleUploadAssetList = () => {
    if (!uploadedFile) return;
    setReviewStatus("loading");
    setIsReviewDataLoading(true);
    setPhase(Phase.REVIEW_ASSET_LIST);
    // First call — no operationKey; server returns one in the response
    dispatch(
      compareAssetLocationFromExcel({
        file: uploadedFile,
        operationKey: null,
        pageNumber: 1,
        pageSize,
        sortBy,
        sortDirection,
      }),
    )
      .unwrap()
      .then((res: any) => {
        const key: string = res?.data?.operationKey ?? "";
        setOperationKey(key);
        setReviewData(res?.data?.rows ?? []);
        setReviewTotal(res?.numberOfRecords ?? 0);
        setChangedCount(res?.data?.changedCount ?? 0);
        setBaselineLessThanTwelveCount(
          res?.data?.baselineLessThanTwelveCount ?? 0,
        );
        setReviewStatus("assetListPreview");
      })
      .catch(() => {
        setReviewStatus("error");
      })
      .finally(() => {
        setIsReviewDataLoading(false);
      });
  };

  const handleConfirmAndProcess = () => {
    if (
      phase === Phase.REVIEW_ASSET_LIST &&
      reviewStatus === "assetListPreview" &&
      baselineLessThanTwelveCount > 0
    ) {
      setReviewStatus("confirmBaselineAndProcess");
      return;
    }
    setPhase(Phase.CONFIRM_ASSET_LIST);
    setConfirmAndProcessStatus("loading");
    dispatch(updateAssetLocationFromExcel({ operationKey }))
      .unwrap()
      .then(() => setConfirmAndProcessStatus("success"))
      .catch(() => setConfirmAndProcessStatus("error"));
  };

  const getModalHeaderTitle = () =>
    resolveModalHeaderTitle(
      phase,
      exportStatus,
      reviewStatus,
      confirmAndProcessStatus,
    );

  const getModalSize = () => {
    if (
      phase === Phase.REVIEW_ASSET_LIST &&
      reviewStatus === "assetListPreview"
    ) {
      return "xl";
    } else {
      return "lg";
    }
  };

  const showOptionsActionConfig: any = [
    {
      label: "Next",
      event: handleNext,
      variant: "primary",
      disabled: !selectedOption,
    },
  ];

  const filterAssetsActionConfig: any = [
    {
      label: "Back",
      event: handleBack,
      variant: "secondary",
    },
    {
      label: "Generate Asset List",
      event: handleGenerateAssetList,
      variant: "primary",
      disabled: !selectedTcCode || !selectedSiteCode,
    },
  ];

  const generateAssetListActionConfig: any = [
    {
      label: "Back",
      event: handleBack,
      variant: "secondary",
      disabled: exportStatus === "loading",
    },
    {
      label: "Done",
      event: handleCloseModal,
      variant: "primary",
      disabled: exportStatus === "loading",
    },
  ];

  const uploadAssetListActionConfig: any = [
    {
      label: "Back",
      event: handleBack,
      variant: "secondary",
    },
    {
      label: "Upload",
      event: handleUploadAssetList,
      variant: "primary",
      disabled: !uploadedFile,
    },
  ];

  const getReviewExtraActions = (): any[] => {
    if (reviewStatus === "assetListPreview") {
      return [
        {
          label: "Confirm & Process",
          event: handleConfirmAndProcess,
          variant: "primary",
        },
      ];
    }
    if (reviewStatus === "error") {
      return [
        {
          label: "Retry",
          event: () => fetchReviewPage(operationKey, pageNumber),
          variant: "primary",
        },
      ];
    }
    if (reviewStatus === "confirmBaselineAndProcess") {
      return [
        {
          label: "Continue anyway",
          event: handleConfirmAndProcess,
          variant: "primary",
        },
      ];
    }
    return [];
  };

  const reviewAssetListActionConfig: any = [
    {
      label: "Back",
      event: handleBack,
      variant: "secondary",
      disabled: reviewStatus === "loading",
    },
    ...getReviewExtraActions(),
  ];

  const confirmAssetListActionConfig: any = [
    {
      label: "Back",
      event: handleBack,
      variant: "secondary",
      disabled: confirmAndProcessStatus === "loading",
    },
    {
      label: confirmAndProcessStatus === "loading" ? "Processing…" : "Done",
      event: handleCloseModal,
      variant: "primary",
      disabled: confirmAndProcessStatus === "loading",
    },
  ];

  const getActionConfig = () => {
    if (phase === Phase.SHOW_OPTIONS) {
      return showOptionsActionConfig;
    } else if (phase === Phase.FILTER_ASSETS) {
      return filterAssetsActionConfig;
    } else if (phase === Phase.GENERATE_ASSET_LIST) {
      return generateAssetListActionConfig;
    } else if (phase === Phase.UPLOAD_ASSET_LIST) {
      return uploadAssetListActionConfig;
    } else if (phase === Phase.REVIEW_ASSET_LIST) {
      return reviewAssetListActionConfig;
    } else if (phase === Phase.CONFIRM_ASSET_LIST) {
      return confirmAssetListActionConfig;
    } else {
      return [];
    }
  };

  return (
    <div>
      {showAssetTaggingModal && (
        <ModalComponent
          showModal={showAssetTaggingModal}
          handleClose={handleCloseModal}
          headerTitle={getModalHeaderTitle()}
          actionConfig={getActionConfig()}
          isViewMode={phase !== Phase.SHOW_OPTIONS}
          size={getModalSize()}
        >
          {phase === Phase.SHOW_OPTIONS && (
            <div>
              <div style={{ padding: 16 }}>
                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      display: "block",
                      fontWeight: 600,
                      marginBottom: 8,
                    }}
                  >
                    What would you like to do?
                  </div>

                  <div className="d-flex flex-column gap-3">
                    <label
                      className="card p-3 w-100"
                      htmlFor="complete-import-type"
                      aria-label="Complete master data"
                    >
                      <div className="d-flex">
                        <div>
                          <input
                            id="complete-import-type"
                            type="radio"
                            name="importType"
                            checked={selectedOption === Options.GENERATE}
                            onChange={() => {
                              setSelectedOption(Options.GENERATE);
                            }}
                            className="me-2"
                          />
                        </div>
                        <div>
                          <div>Generate asset list</div>
                          <div className="text-muted small">
                            Create a downloadable asset list block-wise or
                            site-wise
                          </div>
                        </div>
                      </div>
                    </label>

                    <label
                      className="card p-3 w-100"
                      htmlFor="incremental-import-type"
                      aria-label="Incremental master data"
                    >
                      <div className="d-flex">
                        <div>
                          <input
                            id="incremental-import-type"
                            type="radio"
                            name="importType"
                            checked={selectedOption === Options.PROCESS}
                            onChange={() => {
                              setSelectedOption(Options.PROCESS);
                            }}
                            className="me-2"
                          />
                        </div>
                        <div>
                          <div>Process asset list</div>
                          <div className="text-muted small">
                            Upload a complete asset list to process
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
          {phase === Phase.FILTER_ASSETS && (
            <FormBuilderComponent
              formRef={formRef}
              initialValues={formFilterInitialValues}
              formConfig={filterFormConfig}
              validation={validationSchema}
            />
          )}
          {phase === Phase.GENERATE_ASSET_LIST && (
            <div
              style={{
                padding: "40px 24px",
                textAlign: "center",
                minHeight: 180,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
              }}
            >
              {exportStatus === "loading" && (
                <>
                  <FontAwesomeIcon
                    icon={faSpinner}
                    size="3x"
                    spin
                    style={{ color: "#4a90d9" }}
                  />
                  <div
                    style={{ fontWeight: 600, fontSize: 16, color: "#2d3a4a" }}
                  >
                    Generating asset list…
                  </div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    Please wait while your file is being prepared.
                  </div>
                </>
              )}

              {exportStatus === "success" && (
                <>
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      backgroundColor: "#28a745",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      size="2x"
                      style={{ color: "#fff" }}
                    />
                  </div>
                  <div
                    style={{ fontWeight: 600, fontSize: 16, color: "#2d3a4a" }}
                  >
                    Asset list downloaded
                  </div>
                  {exportSiteLabel && (
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      {exportSiteLabel}
                      {selectedSiteCode ? ` (${selectedSiteCode})` : ""} · saved
                      as an excel file.
                    </div>
                  )}
                </>
              )}

              {exportStatus === "error" && (
                <>
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      backgroundColor: "#dc3545",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faCircleXmark}
                      size="2x"
                      style={{ color: "#fff" }}
                    />
                  </div>
                  <div
                    style={{ fontWeight: 600, fontSize: 16, color: "#2d3a4a" }}
                  >
                    Download failed
                  </div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    Something went wrong. Please go back and try again.
                  </div>
                </>
              )}
            </div>
          )}
          {phase === Phase.UPLOAD_ASSET_LIST && (
            <div style={{ padding: 16 }}>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept={`${excelFileTypes}, text/csv`}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              {/* Drop zone */}
              <button
                type="button"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  display: "block",
                  width: "100%",
                  border: `2px dashed ${isDragging ? "#4a90d9" : "#c8d0dc"}`,
                  borderRadius: 8,
                  padding: "48px 24px",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: isDragging ? "#f0f7ff" : "#fafbfc",
                  transition:
                    "border-color 0.2s ease, background-color 0.2s ease",
                  userSelect: "none",
                }}
              >
                {/* Upload icon */}
                <div style={{ marginBottom: 16 }}>
                  <FontAwesomeIcon
                    icon={faUpload}
                    size="3x"
                    style={{ color: isDragging ? "#4a90d9" : "#8a9bb0" }}
                  />
                </div>

                {uploadedFile ? (
                  /* File selected state */
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 15,
                        marginBottom: 4,
                        color: "#2d3a4a",
                      }}
                    >
                      {uploadedFile.name}
                    </div>
                    <div
                      className="text-muted small"
                      style={{ marginBottom: 8 }}
                    >
                      {(uploadedFile.size / 1024).toFixed(1)} KB
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 16,
                      }}
                    >
                      <button
                        type="button"
                        style={{
                          fontSize: 13,
                          color: "#4a90d9",
                          textDecoration: "underline",
                          cursor: "pointer",
                          background: "none",
                          border: "none",
                          padding: 0,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        Replace file
                      </button>
                      <button
                        type="button"
                        style={{
                          fontSize: 13,
                          color: "#dc3545",
                          textDecoration: "underline",
                          cursor: "pointer",
                          background: "none",
                          border: "none",
                          padding: 0,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadedFile(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                          }
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Empty state */
                  <>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 15,
                        marginBottom: 6,
                        color: "#2d3a4a",
                      }}
                    >
                      Drag &amp; drop your asset list here
                    </div>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      or{" "}
                      <button
                        type="button"
                        style={{
                          color: "#4a90d9",
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontWeight: 500,
                          background: "none",
                          border: "none",
                          padding: 0,
                          fontSize: "inherit",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        browse
                      </button>{" "}
                      to upload — .xlsx or .csv
                    </div>
                  </>
                )}
              </button>
            </div>
          )}
          {phase === Phase.REVIEW_ASSET_LIST && (
            <div>
              {/* ── loading ── */}
              {reviewStatus === "loading" && (
                <div
                  style={{
                    padding: "40px 24px",
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faSpinner}
                    size="3x"
                    spin
                    style={{ color: "#4a90d9" }}
                  />
                  <div
                    style={{ fontWeight: 600, fontSize: 16, color: "#2d3a4a" }}
                  >
                    Uploading &amp; processing…
                  </div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    Please wait while your file is being reviewed.
                  </div>
                </div>
              )}

              {/* ── error ── */}
              {reviewStatus === "error" && (
                <div
                  style={{
                    padding: "40px 24px",
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      backgroundColor: "#dc3545",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faTriangleExclamation}
                      size="2x"
                      style={{ color: "#fff" }}
                    />
                  </div>
                  <div
                    style={{ fontWeight: 600, fontSize: 16, color: "#2d3a4a" }}
                  >
                    Upload failed
                  </div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    Something went wrong while processing your file. Please go
                    back or retry.
                  </div>
                </div>
              )}

              {/* ── assetListPreview ── */}
              {reviewStatus === "assetListPreview" && (
                <div className="inside-table">
                  <div className="my-3">
                    <AssetPreviewTableComponent
                      columns={columns}
                      data={reviewData}
                      totalNumberOfRecords={reviewTotal}
                      pageSize={pageSize}
                      current={pageNumber}
                      onChangePage={handleChangePage}
                      onChangePageSize={handleChangePageSize}
                      onChangeSort={handleChangeSort}
                      fileName={uploadedFile?.name}
                      changedCount={changedCount}
                      changedField="isChanged"
                      changedOnly={changedOnly}
                      onChangedOnlyToggle={handleChangedOnlyToggle}
                      isLoadingData={isReviewDataLoading}
                    />
                  </div>
                </div>
              )}

              {/* ── confirmBaselineAndProcess ── */}
              {reviewStatus === "confirmBaselineAndProcess" && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "3rem 2rem",
                    textAlign: "center",
                    gap: "1rem",
                  }}
                >
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      backgroundColor: "#c0392b",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faTriangleExclamation}
                      size="2x"
                      style={{ color: "#fff" }}
                    />
                  </div>
                  <div
                    style={{ fontWeight: 700, fontSize: 16, color: "#2d3a4a" }}
                  >
                    Some assets have Baseline of 12 or below
                  </div>
                </div>
              )}
            </div>
          )}
          {phase === Phase.CONFIRM_ASSET_LIST && (
            <div
              style={{
                padding: "40px 24px",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
              }}
            >
              {/* ── loading ── */}
              {confirmAndProcessStatus === "loading" && (
                <>
                  <FontAwesomeIcon
                    icon={faSpinner}
                    size="3x"
                    spin
                    style={{ color: "#4a90d9" }}
                  />
                  <div
                    style={{ fontWeight: 700, fontSize: 16, color: "#2d3a4a" }}
                  >
                    Processing asset list...
                  </div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    Validating rows against the current inventory.
                  </div>
                </>
              )}

              {/* ── success ── */}
              {confirmAndProcessStatus === "success" && (
                <>
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      backgroundColor: "#198754",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      size="2x"
                      style={{ color: "#fff" }}
                    />
                  </div>
                  <div
                    style={{ fontWeight: 700, fontSize: 16, color: "#2d3a4a" }}
                  >
                    Asset list processed successfully!
                  </div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    All asset locations have been updated.
                  </div>
                </>
              )}

              {/* ── error ── */}
              {confirmAndProcessStatus === "error" && (
                <>
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      backgroundColor: "#dc3545",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faCircleXmark}
                      size="2x"
                      style={{ color: "#fff" }}
                    />
                  </div>
                  <div
                    style={{ fontWeight: 700, fontSize: 16, color: "#2d3a4a" }}
                  >
                    Processing failed
                  </div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    Something went wrong. Go back and try again.
                  </div>
                </>
              )}
            </div>
          )}
        </ModalComponent>
      )}
    </div>
  );
};

export default AssetTaggingModalComponent;
